import { Controller } from '@hotwired/stimulus';
import { trackInteraction } from '../lib/track_interaction';

export default class extends Controller {
    static values = { presets: Array, saveUrl: String, starterUrl: String };

    static targets = [
        'presetButton',
        'readyView',
        'editView',
        'customView',
        'recipeNavigation',
        'readyModeButton',
        'customModeButton',
        'image',
        'title',
        'description',
        'modifiedBadge',
        'quantity',
        'unitLabel',
        'doughWeight',
        'hydrationMetric',
        'hydrationMetricCard',
        'ingredients',
        'recipeNote',
        'specialInfo',
        'starterNeed',
        'starterReserve',
        'starterSeed',
        'starterFlour',
        'starterWater',
        'starterLink',
        'editTitle',
        'editQuantity',
        'editBasis',
        'editBasisAmount',
        'tinPanel',
        'tinLength',
        'tinWidth',
        'tinHeight',
        'tinVolume',
        'editIngredients',
        'editDoughWeight',
        'editHydrationMetric',
        'customRows',
        'customTotalFlour',
        'customHydration',
        'customDoughWeight',
        'customName',
        'toast'
    ];

    connect() {
        this.currentPreset = null;
        this.currentModel = null;
        this.settings = null;
        this.quantityValue = 1;
        this.modified = false;
        this.customSequence = 0;
        this.toastTimer = null;
        if (this.presetsValue.length > 0) this.activatePreset(this.presetsValue[0].id);
    }

    selectPreset(event) {
        const presetId = Number(event.currentTarget.dataset.presetId);
        this.activatePreset(presetId);
        trackInteraction('calculator_preset_selected', { calculator: 'bread', preset_id: presetId });
    }

    activatePreset(id) {
        const preset = this.presetsValue.find((item) => Number(item.id) === Number(id));
        if (!preset?.recipe_data?.ingredients?.length) return;
        this.currentPreset = preset;
        this.quantityValue = 1;
        this.modified = false;
        this.settings = this.baseSettings(preset.recipe_data);
        this.presetButtonTargets.forEach((button) =>
            button.classList.toggle('is-active', Number(button.dataset.presetId) === Number(id))
        );
        this.showReadyMode();
        this.recalculateReady();
    }

    showReadyMode(event) {
        if (event) trackInteraction('calculator_mode_selected', { calculator: 'bread', mode: 'ready' });
        this.readyViewTarget.classList.remove('hidden');
        this.editViewTarget.classList.add('hidden');
        this.customViewTarget.classList.add('hidden');
        this.recipeNavigationTarget.classList.remove('hidden');
        this.readyModeButtonTarget.classList.add('is-active');
        this.customModeButtonTarget.classList.remove('is-active');
    }

    showCustomMode() {
        trackInteraction('calculator_mode_selected', { calculator: 'bread', mode: 'custom' });
        this.readyViewTarget.classList.add('hidden');
        this.editViewTarget.classList.add('hidden');
        this.customViewTarget.classList.remove('hidden');
        this.recipeNavigationTarget.classList.add('hidden');
        this.readyModeButtonTarget.classList.remove('is-active');
        this.customModeButtonTarget.classList.add('is-active');
        if (!this.customRowsTarget.children.length) this.initializeCustomRecipe();
        this.recalculateCustom();
    }

    editRecipe() {
        this.readyViewTarget.classList.add('hidden');
        this.editViewTarget.classList.remove('hidden');
        this.customViewTarget.classList.add('hidden');
        this.populateEditor();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    cancelEdit() {
        this.showReadyMode();
    }

    applyEdit() {
        this.modified = true;
        this.quantityValue = Math.max(1, Math.round(this.number(this.editQuantityTarget.value)));
        this.readEditorSettings();
        this.showReadyMode();
        this.recalculateReady();
        trackInteraction('calculator_recipe_modified', { calculator: 'bread', preset_id: this.currentPreset.id });
    }

    restoreBase() {
        this.quantityValue = 1;
        this.modified = false;
        this.settings = this.baseSettings(this.currentPreset.recipe_data);
        this.populateEditor();
        this.showToast('Базовый рецепт восстановлен');
    }

    increaseQuantity() {
        this.quantityValue = Math.min(20, this.quantityValue + 1);
        this.recalculateReady();
    }
    decreaseQuantity() {
        this.quantityValue = Math.max(1, this.quantityValue - 1);
        this.recalculateReady();
    }

    baseSettings(recipe) {
        return {
            unitWeight: this.sum(recipe.ingredients),
            basisKey: recipe.ingredients.find((item) => item.role === 'flour')?.key || recipe.ingredients[0]?.key,
            tin: recipe.tin ? { ...recipe.tin } : null
        };
    }

    recalculateReady() {
        this.currentModel = this.buildModel(this.currentPreset.recipe_data, this.settings, this.quantityValue);
        const recipe = this.currentPreset.recipe_data;
        const forms = recipe.unit_forms || ['изделие', 'изделия', 'изделий'];
        this.imageTarget.src = this.currentPreset.image;
        this.imageTarget.alt = this.currentPreset.name;
        this.titleTarget.textContent = this.currentPreset.name;
        this.descriptionTarget.textContent =
            recipe.note || this.currentPreset.description || 'Проверенный базовый рецепт';
        this.modifiedBadgeTarget.classList.toggle('hidden', !this.modified);
        this.quantityTarget.textContent = this.quantityValue;
        this.unitLabelTarget.textContent = this.plural(this.quantityValue, forms);
        this.doughWeightTarget.textContent = `${this.format(this.currentModel.totalDough)} г`;
        this.hydrationMetricTarget.textContent = `${this.format(this.currentModel.hydration)}%`;
        this.hydrationMetricCardTarget.classList.toggle('hidden', recipe.hydration_visible === false);
        this.renderIngredientRows(this.ingredientsTarget, this.currentModel.ingredients);
        this.recipeNoteTarget.textContent = recipe.note || '';
        this.renderSpecialInfo(recipe);
        this.renderStarterSummary();
    }

    buildModel(recipe, settings, quantity = 1) {
        const baseWeight = Math.max(0.0001, this.sum(recipe.ingredients));
        const scale = (Math.max(0.1, settings.unitWeight) / baseWeight) * quantity;
        const ingredients = recipe.ingredients.map((item) => ({
            ...item,
            starter_hydration: item.role === 'starter' ? 100 : item.starter_hydration,
            grams: this.number(item.grams) * scale
        }));
        const metrics = this.metrics(ingredients);
        ingredients.forEach((item) => {
            item.percentage = metrics.totalFlour > 0 ? (item.grams / metrics.totalFlour) * 100 : 0;
        });
        return {
            ingredients,
            totalDough: this.sum(ingredients),
            unitWeight: settings.unitWeight,
            totalFlour: metrics.totalFlour,
            hydration: metrics.hydration
        };
    }

    populateEditor() {
        const recipe = this.currentPreset.recipe_data;
        this.editTitleTarget.textContent = `Настроить: ${this.currentPreset.name}`;
        this.editQuantityTarget.value = this.quantityValue;
        this.editBasisTarget.innerHTML = recipe.ingredients
            .map((item) => `<option value="${this.escape(item.key)}">${this.escape(item.name)}</option>`)
            .join('');
        if (!recipe.ingredients.some((item) => item.key === this.settings.basisKey)) {
            this.settings.basisKey =
                recipe.ingredients.find((item) => item.role === 'flour')?.key || recipe.ingredients[0].key;
        }
        this.editBasisTarget.value = this.settings.basisKey;
        this.syncBasisAmountFromSettings();
        const hasTin = Boolean(recipe.tin);
        this.tinPanelTarget.classList.toggle('hidden', !hasTin);
        if (hasTin) {
            this.tinLengthTarget.value = this.settings.tin.length;
            this.tinWidthTarget.value = this.settings.tin.width;
            this.tinHeightTarget.value = this.settings.tin.height;
            this.renderTinVolume();
        }
        this.recalculateEditPreview();
    }

    readEditorSettings() {
        const recipe = this.currentPreset.recipe_data;
        const quantity = Math.max(1, Math.round(this.number(this.editQuantityTarget.value)));
        const basis = recipe.ingredients.find((item) => item.key === this.editBasisTarget.value);
        const amount = Math.max(0.1, this.number(this.editBasisAmountTarget.value));
        if (basis) {
            const scale = amount / Math.max(0.0001, this.number(basis.grams));
            this.settings.unitWeight = (this.sum(recipe.ingredients) * scale) / quantity;
            this.settings.basisKey = basis.key;
        }
        if (this.settings.tin)
            this.settings.tin = {
                length: this.number(this.tinLengthTarget.value),
                width: this.number(this.tinWidthTarget.value),
                height: this.number(this.tinHeightTarget.value)
            };
    }

    recalculateEditPreview() {
        this.readEditorSettings();
        const quantity = Math.max(1, Math.round(this.number(this.editQuantityTarget.value)));
        const model = this.buildModel(this.currentPreset.recipe_data, this.settings, quantity);
        this.renderIngredientRows(this.editIngredientsTarget, model.ingredients);
        this.editDoughWeightTarget.textContent = `${this.format(model.totalDough)} г`;
        this.editHydrationMetricTarget.textContent = `${this.format(model.hydration)}%`;
    }

    basisChanged() {
        this.settings.basisKey = this.editBasisTarget.value;
        this.syncBasisAmountFromSettings();
        this.recalculateEditPreview();
    }

    syncBasisAmountFromSettings() {
        const recipe = this.currentPreset.recipe_data;
        const basis = recipe.ingredients.find((item) => item.key === this.editBasisTarget.value);
        if (!basis) return;
        const quantity = Math.max(1, Math.round(this.number(this.editQuantityTarget.value)));
        const scale = this.settings.unitWeight / Math.max(0.0001, this.sum(recipe.ingredients));
        this.editBasisAmountTarget.value = this.round(this.number(basis.grams) * scale * quantity);
    }

    updateTin(updatePreview = true) {
        if (!this.currentPreset.recipe_data.tin) return;
        const dimensions = [
            this.number(this.tinLengthTarget.value),
            this.number(this.tinWidthTarget.value),
            this.number(this.tinHeightTarget.value)
        ];
        const volume = dimensions.reduce((result, value) => result * Math.max(0, value), 1) / 1_000_000;
        const base = this.currentPreset.recipe_data.tin;
        const baseVolume = (base.length * base.width * base.height) / 1_000_000;
        this.tinVolumeTarget.textContent = `${this.format(volume)} л`;
        if (volume > 0) {
            this.settings.unitWeight =
                (this.baseSettings(this.currentPreset.recipe_data).unitWeight * volume) / baseVolume;
            this.syncBasisAmountFromSettings();
        }
        if (updatePreview) this.recalculateEditPreview();
    }

    renderTinVolume() {
        const dimensions = [
            this.number(this.tinLengthTarget.value),
            this.number(this.tinWidthTarget.value),
            this.number(this.tinHeightTarget.value)
        ];
        const volume = dimensions.reduce((result, value) => result * Math.max(0, value), 1) / 1_000_000;
        this.tinVolumeTarget.textContent = `${this.format(volume)} л`;
    }

    renderIngredientRows(target, ingredients) {
        target.innerHTML = ingredients
            .map(
                (item) =>
                    `<tr><td><span class="bread-ingredient-icon">${this.ingredientIcon(item.role)}</span>${this.escape(item.name)}</td><td>${this.format(item.grams)} г</td><td>${this.format(item.percentage)}%</td></tr>`
            )
            .join('');
    }

    renderSpecialInfo(recipe) {
        const details = [];
        if (recipe.tin) {
            const tin = this.settings.tin || recipe.tin;
            const volume = (tin.length * tin.width * tin.height) / 1_000_000;
            details.push(
                `<strong>Форма:</strong> ${this.format(tin.length)} × ${this.format(tin.width)} × ${this.format(tin.height)} мм · примерно ${this.format(volume)} л`
            );
        }
        if (recipe.pizza_portions)
            details.push(
                recipe.pizza_portions
                    .map((item) => `${item.count} ${item.label} — примерно по ${item.grams} г`)
                    .join(' · ')
            );
        if (recipe.water_option) {
            const water = this.currentModel.ingredients.find((item) => item.key === recipe.water_option.key);
            const baseWater = recipe.ingredients.find((item) => item.key === recipe.water_option.key);
            if (water && baseWater) {
                const scale = water.grams / Math.max(0.0001, this.number(baseWater.grams));
                const maximum = this.number(recipe.water_option.maximum) * scale;
                details.push(
                    `<strong>Вода:</strong> базово ${this.format(water.grams)} г; для влагоёмкой муки можно постепенно увеличить до ${this.format(maximum)} г`
                );
            }
        }
        const html = details.join('<br>');
        this.specialInfoTarget.innerHTML = html;
        this.specialInfoTarget.classList.toggle('hidden', !html);
    }

    renderStarterSummary() {
        const starter = this.currentModel.ingredients.find((item) => item.role === 'starter');
        if (!starter) return;
        const required = starter.grams;
        const feeding = this.convenientFeeding(required, [1, 2, 2]);
        this.starterNeedTarget.textContent = `${this.format(required)} г`;
        this.starterReserveTarget.textContent = `${this.format(feeding.reserve)} г`;
        this.starterSeedTarget.textContent = `${this.format(feeding.seed)} г`;
        this.starterFlourTarget.textContent = `${this.format(feeding.flour)} г`;
        this.starterWaterTarget.textContent = `${this.format(feeding.water)} г`;
        this.starterLinkTarget.href = `${this.starterUrlValue}?required=${encodeURIComponent(this.round(required))}`;
    }

    initializeCustomRecipe() {
        this.customNameTarget.value = 'Мой рецепт';
        [
            { name: 'Мука пшеничная', role: 'flour', grams: 500 },
            { name: 'Вода', role: 'water', grams: 350 },
            { name: 'Закваска', role: 'starter', grams: 100, starter_hydration: 100 },
            { name: 'Соль', role: 'other', grams: 10 }
        ].forEach((item) => this.addCustomRow(item));
    }

    addIngredient() {
        this.addCustomRow({ name: 'Новый ингредиент', role: 'other', grams: 0 });
        this.recalculateCustom();
    }

    addCustomRow(item) {
        const id = this.customSequence++;
        const row = document.createElement('div');
        row.className = 'custom-ingredient-row';
        row.dataset.customRow = id;
        row.innerHTML = `
      <input class="bread-field" value="${this.escape(item.name)}" aria-label="Название ингредиента" data-custom-name>
      <select class="bread-select" aria-label="Роль ингредиента" data-custom-role data-action="change->bread-calculator#customRoleChanged">${this.roleOptions(item.role)}</select>
      <span class="bread-number-wrap"><input type="number" min="0" step="0.1" value="${this.round(item.grams)}" data-custom-grams data-action="input->bread-calculator#customGramsChanged"><span>г</span></span>
      <span class="bread-number-wrap"><input type="number" min="0" step="0.1" value="0" data-custom-percent data-action="change->bread-calculator#customPercentChanged"><span>%</span></span>
      <button type="button" class="custom-remove" data-action="bread-calculator#removeIngredient" aria-label="Удалить ингредиент">×</button>`;
        this.customRowsTarget.appendChild(row);
    }

    customRoleChanged(event) {
        this.recalculateCustom();
    }

    customGramsChanged() {
        this.recalculateCustom();
    }

    customPercentChanged(event) {
        const row = event.currentTarget.closest('[data-custom-row]');
        const rows = this.customData();
        const current = rows.find((item) => item.id === row.dataset.customRow);
        const percentage = Math.max(0, this.number(event.currentTarget.value)) / 100;
        const metrics = this.metrics(rows);
        let grams = percentage * metrics.totalFlour;
        if (current.role === 'flour') {
            const otherFlour = Math.max(0, metrics.totalFlour - current.grams);
            grams = percentage >= 0.999 ? current.grams : (percentage * otherFlour) / Math.max(0.0001, 1 - percentage);
        } else if (current.role === 'starter') {
            const fraction = 0.5;
            const otherFlour = Math.max(0, metrics.totalFlour - current.grams * fraction);
            grams = (percentage * otherFlour) / Math.max(0.0001, 1 - percentage * fraction);
        }
        row.querySelector('[data-custom-grams]').value = this.round(grams);
        this.recalculateCustom();
    }

    removeIngredient(event) {
        event.currentTarget.closest('[data-custom-row]').remove();
        this.recalculateCustom();
    }

    recalculateCustom() {
        const ingredients = this.customData(),
            metrics = this.metrics(ingredients);
        this.customRowsTarget.querySelectorAll('[data-custom-row]').forEach((row) => {
            const item = ingredients.find((ingredient) => ingredient.id === row.dataset.customRow);
            row.querySelector('[data-custom-percent]').value =
                metrics.totalFlour > 0 ? this.round((item.grams / metrics.totalFlour) * 100) : 0;
        });
        this.customTotalFlourTarget.textContent = `${this.format(metrics.totalFlour)} г`;
        this.customHydrationTarget.textContent = metrics.totalFlour > 0 ? `${this.format(metrics.hydration)}%` : '—';
        this.customDoughWeightTarget.textContent = `${this.format(this.sum(ingredients))} г`;
    }

    customData() {
        return Array.from(this.customRowsTarget.querySelectorAll('[data-custom-row]')).map((row) => {
            const role = row.querySelector('[data-custom-role]').value;
            return {
                id: row.dataset.customRow,
                key: `custom_${row.dataset.customRow}`,
                name: row.querySelector('[data-custom-name]').value.trim() || 'Ингредиент',
                role,
                grams: Math.max(0, this.number(row.querySelector('[data-custom-grams]').value)),
                starter_hydration: role === 'starter' ? 100 : undefined,
                water_fraction: role === 'liquid' ? 0.87 : undefined
            };
        });
    }

    roleOptions(selected) {
        return [
            ['flour', 'Мука'],
            ['water', 'Вода'],
            ['starter', 'Закваска'],
            ['liquid', 'Молоко / жидкость'],
            ['soak_water', 'Вода замачивания'],
            ['other', 'Прочее']
        ]
            .map(
                ([value, label]) => `<option value="${value}" ${value === selected ? 'selected' : ''}>${label}</option>`
            )
            .join('');
    }

    metrics(ingredients) {
        let totalFlour = 0,
            hydrationWater = 0;
        ingredients.forEach((item) => {
            const grams = this.number(item.grams);
            if (item.role === 'flour') totalFlour += grams;
            if (item.role === 'water') hydrationWater += grams;
            if (item.role === 'liquid') hydrationWater += grams * this.number(item.water_fraction);
            if (item.role === 'starter') {
                const flour = grams / 2;
                totalFlour += flour;
                hydrationWater += grams - flour;
            }
        });
        return { totalFlour, hydration: totalFlour > 0 ? (hydrationWater / totalFlour) * 100 : 0 };
    }

    async copy() {
        const rows = this.currentModel.ingredients.map((item) => `${item.name}: ${this.format(item.grams)} г`);
        await this.copyText(
            [
                this.currentPreset.name,
                ...rows,
                `Общий вес теста: ${this.format(this.currentModel.totalDough)} г`,
                `Гидратация: ${this.format(this.currentModel.hydration)}%`
            ].join('\n')
        );
    }

    async copyCustom() {
        const ingredients = this.customData(),
            metrics = this.metrics(ingredients);
        await this.copyText(
            [
                this.customNameTarget.value || 'Мой рецепт',
                ...ingredients.map((item) => `${item.name}: ${this.format(item.grams)} г`),
                `Общий вес теста: ${this.format(this.sum(ingredients))} г`,
                `Гидратация: ${this.format(metrics.hydration)}%`
            ].join('\n')
        );
    }

    async copyText(value) {
        try {
            await navigator.clipboard.writeText(value);
            this.showToast('Рецепт скопирован');
            trackInteraction('calculator_result_copied', { calculator: 'bread' });
        } catch (_) {
            this.showToast('Не удалось скопировать рецепт', true);
        }
    }

    async save() {
        const isCustom = !this.customViewTarget.classList.contains('hidden');
        const ingredients = isCustom ? this.customData() : this.currentModel.ingredients;
        const metrics = this.metrics(ingredients);
        const defaultName = isCustom
            ? this.customNameTarget.value || 'Мой рецепт'
            : `${this.currentPreset.name} — ${new Date().toLocaleDateString('ru-RU')}`;
        const name = window.prompt('Название рецепта', defaultName);
        if (!name?.trim()) return;
        const body = {
            saved_bread_recipe: {
                bread_preset_id: isCustom ? null : this.currentPreset.id,
                name: name.trim(),
                calculation_basis: isCustom ? 'custom' : 'recipe',
                target_weight: this.sum(ingredients),
                parameters: {
                    mode: isCustom ? 'custom' : 'recipe',
                    quantity: isCustom ? 1 : this.quantityValue,
                    ingredients
                },
                result: {
                    ingredients: ingredients.map((item) => ({
                        name: item.name,
                        grams: this.round(item.grams),
                        percentage: metrics.totalFlour ? this.round((item.grams / metrics.totalFlour) * 100) : 0
                    })),
                    total_dough: this.round(this.sum(ingredients)),
                    true_hydration: this.round(metrics.hydration),
                    flour: this.round(metrics.totalFlour)
                }
            }
        };
        try {
            const response = await fetch(this.saveUrlValue, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
                },
                body: JSON.stringify(body)
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Не удалось сохранить рецепт');
            this.showToast(data.message || 'Рецепт сохранён');
        } catch (error) {
            this.showToast(error.message, true);
        }
    }

    print() {
        trackInteraction('calculator_result_printed', { calculator: 'bread' });
        window.print();
    }
    ingredientIcon(role) {
        return { flour: '◇', water: '◉', starter: '◫', liquid: '◉', soak_water: '○', other: '◆' }[role] || '◆';
    }

    convenientFeeding(required, ratio) {
        const totalParts = ratio.reduce((sum, value) => sum + value, 0);
        const onePart = Math.max(5, Math.ceil((required + 20) / totalParts / 5) * 5);
        const [seed, flour, water] = ratio.map((value) => value * onePart);
        const total = seed + flour + water;
        return { seed, flour, water, total, reserve: total - required };
    }

    plural(count, forms) {
        const mod100 = count % 100,
            mod10 = count % 10;
        if (mod100 >= 11 && mod100 <= 19) return forms[2];
        if (mod10 === 1) return forms[0];
        if (mod10 >= 2 && mod10 <= 4) return forms[1];
        return forms[2];
    }
    sum(items) {
        return items.reduce((sum, item) => sum + this.number(item.grams), 0);
    }
    number(value) {
        const result = Number.parseFloat(value);
        return Number.isFinite(result) ? result : 0;
    }
    round(value) {
        return Math.round((this.number(value) + Number.EPSILON) * 10) / 10;
    }
    round2(value) {
        return Math.round((this.number(value) + Number.EPSILON) * 100) / 100;
    }
    format(value) {
        return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(this.number(value));
    }
    escape(value) {
        const element = document.createElement('div');
        element.textContent = String(value ?? '');
        return element.innerHTML.replaceAll('"', '&quot;');
    }

    showToast(message, error = false) {
        clearTimeout(this.toastTimer);
        this.toastTarget.textContent = message;
        this.toastTarget.classList.toggle('bg-[#945b50]', error);
        this.toastTarget.classList.toggle('bg-[#4f3a2b]', !error);
        this.toastTarget.classList.remove('opacity-0', 'translate-y-3');
        this.toastTimer = setTimeout(() => this.toastTarget.classList.add('opacity-0', 'translate-y-3'), 2800);
    }
}
